// Libraries
import { Box, Link, Text } from '@chakra-ui/react';
import NextLink from 'next/link';
// Components
import { RatingsIcon } from '@atoms/index';
// Utils
import { AppPath } from '@constants/index';
// Types
import {
  FooterCellLinkProps,
  RatingHeaderProps,
  SummaryCellTableProps,
  VesselSegmentCellProps,
} from './SummaryTable.types';

/**
 * SummaryCellTable component displays a summary cell in a table.
 *
 * @component
 * @param {SummaryCellTableProps} props - The component props.
 * @param {string} props.eoyRating - The end-of-year rating.
 * @param {string} props.ytdRating - The year-to-date rating.
 * @param {number} props.pl - The left padding value.
 * @param {number} props.pr - The right padding value.
 * @param {number} props.borderStartRadius - The border start radius value.
 * @param {number} props.borderEndRadius - The border end radius value.
 * @returns {ReactElement} SummaryCellTable component.
 */
export const SummaryCellTable = ({
  eoyRating,
  ytdRating,
  pl,
  pr,
  borderStartRadius,
  borderEndRadius,
}: SummaryCellTableProps) => (
  <Box pl={pl} pr={pr} textAlign='center'>
    <Text fontSize='s' fontWeight='medium' lineHeight={2}>
      {ytdRating}
    </Text>
    <Text
      fontSize='s'
      lineHeight={2}
      backgroundColor='table.cell'
      borderStartRadius={borderStartRadius}
      borderEndRadius={borderEndRadius}
    >
      {eoyRating}
    </Text>
  </Box>
);

/**
 * SummaryCellTable component displays a summary cell in a table.
 *
 * @component
 * @param {SummaryCellTableProps} props - The component props.
 * @param {string} props.eoyRating - The end-of-year rating.
 * @param {string} props.ytdRating - The year-to-date rating.
 * @param {number} props.pl - The left padding value.
 * @param {number} props.pr - The right padding value.
 * @param {number} props.borderStartRadius - The border start radius value.
 * @param {number} props.borderEndRadius - The border end radius value.
 * @returns {ReactElement} SummaryCellTable component.
 */
export const FooterCellLink = ({
  eoyRating,
  ytdRating,
  pl,
  pr,
  borderStartRadius,
  borderEndRadius,
  rating,
}: FooterCellLinkProps) => {
  return (
    <Box pl={pl} pr={pr} textAlign='center'>
      <NextLink href={`${AppPath.FLEET_CII_RATINGS}?periodCii=${rating}`} passHref legacyBehavior>
        <Link fontSize='s' lineHeight={2} px={1}>
          {ytdRating}
        </Link>
      </NextLink>
      <br />
      <Box backgroundColor='table.cell' borderStartRadius={borderStartRadius} borderEndRadius={borderEndRadius}>
        <NextLink href={`${AppPath.FLEET_CII_RATINGS}?forecastedCiiRating=${rating}`} passHref legacyBehavior>
          <Link fontSize='s' lineHeight={2} px={1}>
            {eoyRating}
          </Link>
        </NextLink>
      </Box>
    </Box>
  );
};

/**
 * RatingHeader component represents a rating header in the summary table.
 *
 * @component
 * @param {RatingHeaderProps} props - The component props.
 * @param {string} props.rating - The rating value.
 * @param {number} props.pl - The left padding value.
 * @param {number} props.pr - The right padding value.
 * @returns {ReactElement} RatingHeader component.
 */
export const RatingHeader = ({ rating, pl, pr }: RatingHeaderProps) => (
  <Box py={2} textAlign='center' pl={pl} pr={pr}>
    <RatingsIcon rating={rating} dark w={4} h={4} />
  </Box>
);

/**
 * VesselSegmentCell component displays a vessel segment cell in a table.
 *
 * @component
 * @param {VesselSegmentCellProps} props - The component props.
 * @param {string} props.vesselType - The type of vessel.
 * @param {number} props.total - The total count.
 * @returns {ReactElement} VesselSegmentCell component.
 */
export const VesselSegmentCell = ({ vesselType, total }: VesselSegmentCellProps) => (
  <Box p={2}>
    <NextLink
      href={`${AppPath.FLEET_CII_RATINGS}?vesselType=${encodeURIComponent(vesselType)}`}
      passHref
      legacyBehavior
    >
      <Link fontSize='s' lineHeight={2} px={1} ml={-1}>
        {vesselType}
      </Link>
    </NextLink>
    <Text fontSize='s' variant='faded' lineHeight={2}>
      Total: {total}
    </Text>
  </Box>
);
